import React, { useState } from 'react';
import { Phone, MessageCircle, Mail, Instagram, MapPin, Clock, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PHONE = '503-328-4130';
const EMAIL = 'pdxwindowtint@gmail.com';
const INSTAGRAM_URL = 'https://www.instagram.com/PDXWindowTint';

export const Footer = () => {
  const [showCallModal, setShowCallModal] = useState(false);
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="footer-section" style={{ background: '#000000' }}>

      {/* ── CONTACT INFO section ── */}
      <div style={{ paddingTop: '6px', paddingBottom: '12px', paddingLeft: '20px', paddingRight: '20px' }}>

        <h2
          data-testid="contact-section-heading"
          className="font-black uppercase text-center"
          style={{
            color: '#eab308',
            fontSize: 'clamp(1rem, 4vw, 1.2rem)',
            letterSpacing: '0.1em',
            marginBottom: '14px',
          }}
        >
          CONTACT INFO
        </h2>

        {/* Three side-by-side contact buttons — icon + label on same row */}
        <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto" style={{ marginBottom: '18px' }}>

          {/* Call/Text */}
          <button
            data-testid="contact-call-text-btn"
            onClick={() => setShowCallModal(true)}
            className="flex items-center justify-center gap-2 rounded-2xl transition-colors"
            style={{
              background: '#111113',
              border: '1px solid rgba(255,255,255,0.10)',
              padding: '16px 8px',
              boxShadow: '0 8px 20px rgba(0,0,0,0.5)',
            }}
          >
            <Phone className="w-5 h-5 flex-shrink-0" style={{ color: '#22c55e' }} strokeWidth={2} />
            <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: 700, letterSpacing: '0.2px' }}>Call/Text</span>
          </button>

          {/* Email */}
          <a
            href={`mailto:${EMAIL}`}
            data-testid="contact-email-btn"
            className="flex items-center justify-center gap-2 rounded-2xl transition-colors"
            style={{
              background: '#111113',
              border: '1px solid rgba(255,255,255,0.10)',
              padding: '16px 8px',
              boxShadow: '0 8px 20px rgba(0,0,0,0.5)',
              textDecoration: 'none',
            }}
          >
            <Mail className="w-5 h-5 flex-shrink-0" style={{ color: '#3b82f6' }} strokeWidth={2} />
            <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: 700, letterSpacing: '0.2px' }}>Email</span>
          </a>

          {/* Instagram */}
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-instagram-btn"
            className="flex items-center justify-center gap-2 rounded-2xl transition-colors"
            style={{
              background: '#111113',
              border: '1px solid rgba(255,255,255,0.10)',
              padding: '16px 8px',
              boxShadow: '0 8px 20px rgba(0,0,0,0.5)',
              textDecoration: 'none',
            }}
          >
            <Instagram className="w-5 h-5 flex-shrink-0" style={{ color: '#c13584' }} strokeWidth={2} />
            <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: 700, letterSpacing: '0.2px' }}>Instagram</span>
          </a>
        </div>

        {/* Business hours pills — gold border, clock icon, gold text */}
        <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto" style={{ marginBottom: '18px' }}>
          <div
            data-testid="contact-hours-weekday"
            className="flex items-center justify-center gap-2 rounded-full"
            style={{
              background: '#0d0d0d',
              border: '1px solid rgba(234,179,8,0.45)',
              padding: '11px 10px',
            }}
          >
            <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#eab308' }} strokeWidth={1.8} />
            <span style={{ color: '#eab308', fontSize: '12px', fontWeight: 700 }}>Mon–Fri  4 – 8 PM</span>
          </div>
          <div
            data-testid="contact-hours-weekend"
            className="flex items-center justify-center gap-2 rounded-full"
            style={{
              background: '#0d0d0d',
              border: '1px solid rgba(234,179,8,0.45)',
              padding: '11px 10px',
            }}
          >
            <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#eab308' }} strokeWidth={1.8} />
            <span style={{ color: '#eab308', fontSize: '12px', fontWeight: 700 }}>Sat–Sun  11 AM – 5 PM</span>
          </div>
        </div>

        {/* Location */}
        <div
          className="flex items-center justify-center gap-2"
          data-testid="contact-location-text"
        >
          <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: '#a1a1aa' }} strokeWidth={1.8} />
          <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: 600 }}>Portland, Oregon</span>
        </div>
      </div>

      {/* ── Copyright bar ── */}
      <div
        style={{
          paddingTop: '10px',
          paddingBottom: '20px',
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      >
        <div className="flex items-center justify-center gap-2">
          <span
            onClick={() => navigate('/admin')}
            data-testid="footer-copyright"
            style={{
              color: '#71717a',
              fontSize: '12px',
              fontWeight: 400,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            &copy; {year} PDX Window Tint
          </span>

          <span style={{ color: '#3f3f46', fontSize: '12px' }}>·</span>

          <a
            href="/privacy"
            data-testid="footer-privacy-link"
            style={{
              color: '#71717a',
              fontSize: '12px',
              fontWeight: 400,
              textDecoration: 'none',
            }}
          >
            Privacy
          </a>

          <span style={{ color: '#3f3f46', fontSize: '12px' }}>·</span>

          <a
            href="/simulator"
            data-testid="footer-simulator-link"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo(0, 0);
              setTimeout(() => window.location.replace('/simulator'), 50);
            }}
            style={{
              color: '#71717a',
              fontSize: '12px',
              fontWeight: 400,
              textDecoration: 'none',
            }}
          >
            Tint Sim
          </a>
        </div>
      </div>

      {/* ── Call / Text modal ── */}
      {showCallModal && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 9999, background: 'rgba(0,0,0,0.80)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowCallModal(false)}
        >
          <div
            className="w-full max-w-xs rounded-2xl"
            style={{
              background: '#0a0a0c',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: '0 10px 22px rgba(0,0,0,0.55), 0 2px 4px rgba(0,0,0,0.4)',
              padding: '28px 24px 20px',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="call-text-modal"
          >
            {/* Close */}
            <button
              onClick={() => setShowCallModal(false)}
              data-testid="call-text-modal-close"
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <X className="w-4 h-4" style={{ color: '#a1a1aa' }} />
            </button>

            <p
              className="text-center font-bold"
              style={{
                color: '#ffffff',
                fontSize: '16px',
                marginBottom: '24px',
                letterSpacing: '0.01em',
              }}
            >
              How would you like to reach me?
            </p>

            <div className="flex gap-3" style={{ marginBottom: '16px' }}>
              {/* Call */}
              <a
                href={`tel:${PHONE}`}
                data-testid="call-option-btn"
                className="flex-1 flex flex-col items-center gap-2.5 rounded-2xl transition-colors"
                style={{
                  background: 'rgba(34,197,94,0.10)',
                  border: '1px solid rgba(34,197,94,0.30)',
                  padding: '22px 12px',
                  textDecoration: 'none',
                }}
              >
                <Phone className="w-7 h-7" style={{ color: '#22c55e' }} strokeWidth={1.8} />
                <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: 700 }}>Call</span>
              </a>

              {/* Text */}
              <a
                href={`sms:${PHONE}`}
                data-testid="text-option-btn"
                className="flex-1 flex flex-col items-center gap-2.5 rounded-2xl transition-colors"
                style={{
                  background: 'rgba(59,130,246,0.10)',
                  border: '1px solid rgba(59,130,246,0.30)',
                  padding: '22px 12px',
                  textDecoration: 'none',
                }}
              >
                <MessageCircle className="w-7 h-7" style={{ color: '#60a5fa' }} strokeWidth={1.8} />
                <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: 700 }}>Text</span>
              </a>
            </div>

            <button
              onClick={() => setShowCallModal(false)}
              data-testid="call-text-cancel-btn"
              style={{
                width: '100%',
                textAlign: 'center',
                color: '#71717a',
                fontSize: '13px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </footer>
  );
};
